import { useEffect, useMemo, useState } from "react"

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: "accepted" | "dismissed"
    platform: string
  }>
}

type Platform = "ios" | "android" | "other"

export type PwaInstallEnvironment = {
  getUserAgent: () => string
  isIosStandalone: () => boolean
  isStandaloneDisplayMode: () => boolean
  isBrave: () => boolean
  getHref: () => string
  writeClipboardText: (text: string) => Promise<void>
}

export type UsePwaInstallOptions = {
  isVisible?: boolean
  storageKey?: string
  showAlways?: boolean
  platform?: Platform
  deferredPrompt?: BeforeInstallPromptEvent | null
  onDismiss?: () => void
  environment?: Partial<PwaInstallEnvironment>
}

export type UsePwaInstallResult = {
  platform: Platform
  isInstalled: boolean
  showPwaInfo: boolean

  shouldRenderNotification: boolean
  showNotification: boolean
  shouldRenderInstructions: boolean
  showInstructions: boolean

  isIncompatibleBrowser: boolean
  isAndroidInstallable: boolean
  hasNativePrompt: boolean
  linkCopied: boolean
  errorMessage: string | null

  openInstructions: () => void
  dismiss: () => void
  closeInstructions: () => void
  copyLink: () => Promise<void>
  nativeInstall: () => Promise<void>
}

const PROMPT_CAPTURED_EVENT = "tutellus:pwa-prompt-captured"

let globalDeferredPrompt: BeforeInstallPromptEvent | null = null
let isListenerRegistered = false

function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return globalDeferredPrompt
}

function setDeferredPrompt(prompt: BeforeInstallPromptEvent | null): void {
  globalDeferredPrompt = prompt
}

function registerBeforeInstallPromptListener(): void {
  if (typeof window === "undefined") return
  if (isListenerRegistered) return
  isListenerRegistered = true

  const handleBeforeInstallPrompt = (e: Event) => {
    // Prevent automatic prompt; allow triggering it from user interaction.
    e.preventDefault()
    const prompt = e as BeforeInstallPromptEvent
    if (prompt && typeof prompt.prompt === "function") {
      setDeferredPrompt(prompt)
      window.dispatchEvent(new CustomEvent(PROMPT_CAPTURED_EVENT, { detail: prompt }))
    }
  }

  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt, { capture: true, passive: false })
}

function getDefaultEnvironment(): PwaInstallEnvironment {
  return {
    getUserAgent: () => (typeof window !== "undefined" ? window.navigator.userAgent : ""),
    isIosStandalone: () =>
      typeof window !== "undefined" && (window.navigator as Navigator & { standalone?: boolean }).standalone === true,
    isStandaloneDisplayMode: () =>
      typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches === true,
    isBrave: () => {
      if (typeof navigator === "undefined") return false
      // Brave exposes navigator.brave (on some platforms/browsers)
      return "brave" in navigator && (navigator as Navigator & { brave?: unknown }).brave !== undefined
    },
    getHref: () => (typeof window !== "undefined" ? window.location.href : ""),
    writeClipboardText: async (text: string) => {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }

      // Fallback
      if (typeof document === "undefined") throw new Error("Clipboard unavailable")
      const textArea = document.createElement("textarea")
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      const ok = document.execCommand("copy")
      document.body.removeChild(textArea)
      if (!ok) throw new Error("Copy failed")
    },
  }
}

function detectPlatform(userAgent: string): Platform {
  const ua = userAgent.toLowerCase()
  const isIOS = /iphone|ipad|ipod/.test(ua)
  const isAndroid = /android/.test(ua)

  if (isIOS) return "ios"
  if (isAndroid) return "android"
  return "other"
}

function isAppInstalled(env: PwaInstallEnvironment): boolean {
  return env.isIosStandalone() || env.isStandaloneDisplayMode()
}

function useLocalStorageBoolean(key: string, initialValue: boolean, persist: boolean): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (!persist) return initialValue
    try {
      if (typeof window === "undefined") return initialValue
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as boolean) : initialValue
    } catch {
      return initialValue
    }
  })

  const setAndPersist = (next: boolean) => {
    setValue(next)
    if (!persist) return
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(next))
      }
    } catch {
      // ignore
    }
  }

  return [value, setAndPersist]
}

export function usePwaInstall(options: UsePwaInstallOptions = {}): UsePwaInstallResult {
  const {
    isVisible = true,
    storageKey = "showPWAInfo",
    showAlways = false,
    platform: platformOverride,
    deferredPrompt: deferredPromptProp,
    onDismiss,
    environment: environmentOverride,
  } = options

  const env = useMemo<PwaInstallEnvironment>(() => {
    return { ...getDefaultEnvironment(), ...(environmentOverride ?? {}) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [showPwaInfo, setShowPwaInfo] = useLocalStorageBoolean(storageKey, true, !showAlways)
  const [deferredPrompt, setDeferredPromptState] = useState<BeforeInstallPromptEvent | null>(
    deferredPromptProp ?? getDeferredPrompt() ?? null,
  )

  const userAgent = env.getUserAgent()
  const platform = platformOverride ?? detectPlatform(userAgent)
  const isInstalled = isAppInstalled(env)

  const initialVisible = isVisible && showPwaInfo && !isInstalled
  const [showNotification, setShowNotification] = useState<boolean>(initialVisible)
  const [shouldRenderNotification, setShouldRenderNotification] = useState<boolean>(initialVisible)

  const [showInstructions, setShowInstructions] = useState<boolean>(false)
  const [shouldRenderInstructions, setShouldRenderInstructions] = useState<boolean>(false)

  const [isIncompatibleBrowser, setIsIncompatibleBrowser] = useState<boolean>(false)
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Keep state in sync if consumer injects a prompt.
  useEffect(() => {
    if (deferredPromptProp) {
      setDeferredPromptState(deferredPromptProp)
    }
  }, [deferredPromptProp])

  useEffect(() => {
    if (typeof window === "undefined") return

    registerBeforeInstallPromptListener()

    const showNotificationIfNeeded = () => {
      if (isInstalled || !isVisible || !showPwaInfo) return
      // While the instructions sheet is (or will be) shown, never re-show the notify.
      if (showInstructions || shouldRenderInstructions) return
      if (showNotification || shouldRenderNotification) return

      if (platform === "ios" || platform === "android") {
        setShowNotification(true)
        setShouldRenderNotification(true)
      }
    }

    const globalPrompt = getDeferredPrompt()
    if (globalPrompt && !deferredPrompt) {
      setDeferredPromptState(globalPrompt)
    }

    showNotificationIfNeeded()

    const handlePromptCaptured = (e: Event) => {
      const ce = e as CustomEvent
      const prompt = ce.detail as BeforeInstallPromptEvent
      if (prompt && typeof prompt.prompt === "function") {
        setDeferredPromptState(prompt)
        showNotificationIfNeeded()
      }
    }

    window.addEventListener(PROMPT_CAPTURED_EVENT, handlePromptCaptured as EventListener)

    return () => {
      window.removeEventListener(PROMPT_CAPTURED_EVENT, handlePromptCaptured as EventListener)
    }
  }, [
    deferredPrompt,
    deferredPromptProp,
    env,
    isInstalled,
    isVisible,
    platform,
    showNotification,
    showInstructions,
    showPwaInfo,
    shouldRenderInstructions,
    shouldRenderNotification,
  ])

  const prompt = deferredPromptProp ?? deferredPrompt ?? getDeferredPrompt()
  const hasNativePrompt = prompt != null && typeof prompt.prompt === "function"
  const isChromeAndroid = !env.isBrave() && /Chrome/.test(userAgent) && platform === "android" && !isInstalled
  const isAndroidInstallable = hasNativePrompt || isChromeAndroid

  const openInstructions = () => {
    const ua = env.getUserAgent().toLowerCase()
    const isIOS = /iphone|ipad|ipod/.test(ua)
    const isAndroid = /android/.test(ua)
    const isChromeUA = /chrome|crios/.test(ua) && !env.isBrave()
    const isSafariUA = /safari/.test(ua) && !/chrome|crios|fxios|edgios|opr/.test(ua) && !env.isBrave()
    const hasPromptNow = deferredPromptProp ?? deferredPrompt ?? getDeferredPrompt()

    const isCompatible = hasPromptNow || (isAndroid && isChromeUA) || (isIOS && (isSafariUA || isChromeUA))
    setIsIncompatibleBrowser(!isCompatible)

    setShowNotification(false)
    setShouldRenderInstructions(true)

    setTimeout(() => {
      setShouldRenderNotification(false)
      requestAnimationFrame(() => {
        setShowInstructions(true)
      })
    }, 300)
  }

  const dismiss = () => {
    setShowNotification(false)
    setShowPwaInfo(false)
    setTimeout(() => {
      setShouldRenderNotification(false)
      setShowInstructions(false)
      setShouldRenderInstructions(false)
      setIsIncompatibleBrowser(false)
      setLinkCopied(false)
      onDismiss?.()
    }, 300)
  }

  const closeInstructions = () => {
    setShowInstructions(false)
    setLinkCopied(false)
    setShowNotification(false)
    setShowPwaInfo(false)
    setTimeout(() => {
      setShouldRenderInstructions(false)
      setShouldRenderNotification(false)
      setIsIncompatibleBrowser(false)
      onDismiss?.()
    }, 300)
  }

  const copyLink = async () => {
    try {
      await env.writeClipboardText(env.getHref())
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      // swallow
    }
  }

  const nativeInstall = async () => {
    setErrorMessage(null)

    let p = deferredPromptProp ?? deferredPrompt ?? getDeferredPrompt()
    if (!p) {
      await new Promise((r) => setTimeout(r, 100))
      p = deferredPromptProp ?? deferredPrompt ?? getDeferredPrompt()
    }

    if (!p || typeof p.prompt !== "function") {
      setErrorMessage(
        "No se puede instalar en este momento. Busca el icono de instalación (➕) en la barra de direcciones o usa el menú del navegador (⋮) → Instalar app",
      )
      setTimeout(() => setErrorMessage(null), 8000)
      return
    }

    try {
      await p.prompt()
      const choice = await p.userChoice
      if (choice?.outcome === "accepted") {
        setDeferredPrompt(null)
        setDeferredPromptState(null)
        dismiss()
      } else {
        closeInstructions()
      }
    } catch {
      setErrorMessage(
        "Error al mostrar el diálogo de instalación. Por favor, usa el menú del navegador (⋮) → Instalar app",
      )
      setTimeout(() => setErrorMessage(null), 5000)
    }
  }

  // If installed, nothing should render.
  if (isInstalled) {
    return {
      platform,
      isInstalled,
      showPwaInfo,
      shouldRenderNotification: false,
      showNotification: false,
      shouldRenderInstructions: false,
      showInstructions: false,
      isIncompatibleBrowser: false,
      isAndroidInstallable: false,
      hasNativePrompt: false,
      linkCopied: false,
      errorMessage: null,
      openInstructions,
      dismiss,
      closeInstructions,
      copyLink,
      nativeInstall,
    }
  }

  return {
    platform,
    isInstalled,
    showPwaInfo,
    shouldRenderNotification,
    showNotification,
    shouldRenderInstructions,
    showInstructions,
    isIncompatibleBrowser,
    isAndroidInstallable,
    hasNativePrompt,
    linkCopied,
    errorMessage,
    openInstructions,
    dismiss,
    closeInstructions,
    copyLink,
    nativeInstall,
  }
}
